import React, { useContext, useEffect } from "react";
import { IconButton, Checkbox, LinearProgress, ColorPicker, IJSXOption } from "@evergis/ui";
import { AggregationFunction } from "@evergis/api";
import { useTranslation } from "react-i18next";
import { clearDateFilterConditions } from "ducks/dateFilter/dateFilter";
import { useDispatch } from "react-redux";
import { formatDate } from "utils/date";
import {
  Card,
  Select,
  SelectLayerContainer,
  SelectAttributeContainer,
  CalculateDescription,
  CalculationContainer,
  UploadDateButton,
  Line,
  SecondLineLayout,
  SecondSelectLayerContainer,
  ColorPickerContainer,
} from "../styled";
import { useGetMaxDateMinDate, useCallbackLayerDispatchers } from "../hooks";
import { DateFilterContext } from "../context";
import { ActionLayerKind, Layer } from "../reducers";
import { useTableFilters } from "../../../hooks/table/useTableFilters";

const extractLayerAttribtesToSelectOptions = (layer: Layer): IJSXOption[] => {
  return layer.attributes && layer.attributes.map(attr => ({ value: attr, text: attr, id: attr }));
};

const extractLayerAggrationAttribtesToSelectOptions = (layer: Layer): IJSXOption[] =>
  layer.aggregationAttributes &&
  layer.aggregationAttributes.map(attr => ({ value: attr.name, text: attr.name, id: attr.name }));

const LayerSelection = ({ layer, selectLayer, options, dialogContainer, loadingOptions, t }) => {
  return (
    <SelectLayerContainer>
      <Select
        options={options}
        width="29rem"
        value={layer.value || ""}
        label={t("layer")}
        placeholder={t("selectCurrentLayer")}
        onChange={selectLayer}
        root={dialogContainer}
      />
      {loadingOptions && (
        <CalculationContainer>
          <LinearProgress />
        </CalculationContainer>
      )}
    </SelectLayerContainer>
  );
};

const AttributeSelection = ({ layer, selectAttribute, loading, setFormRangeDate, minMax, t, dialogContainer }) => {
  return (
    <SelectAttributeContainer>
      <Select
        disabled={!layer.attributes}
        options={extractLayerAttribtesToSelectOptions(layer)}
        width="14rem"
        value={layer.attribute || ""}
        onChange={selectAttribute}
        label={t("attribute")}
        placeholder={t("selectAttribute")}
        root={dialogContainer}
      />
      {loading && (
        <CalculationContainer>
          <CalculateDescription>{t("calculation")}... </CalculateDescription>
          <LinearProgress />
        </CalculationContainer>
      )}
      {loading === false && (
        <CalculationContainer>
          <UploadDateButton
            primary
            kind="updating"
            onClick={() => {
              setFormRangeDate(minMax);
            }}
          >
            {`${formatDate(minMax[0])} - ${formatDate(minMax[1])}`}
          </UploadDateButton>
        </CalculationContainer>
      )}
    </SelectAttributeContainer>
  );
};

const useSetFirstExistDateRange = minMax => {
  const { setFormRangeDate, formRangeDate } = useContext(DateFilterContext);

  useEffect(() => {
    if (formRangeDate.every(date => date === null) && minMax.every(date => date !== null)) {
      setFormRangeDate(minMax);
    }
  }, [formRangeDate, minMax, setFormRangeDate]);
};

interface SedondLine {
  layer: Layer;
  dialogContainer: HTMLElement;
  selectAggregationAttribute: (items: IJSXOption[]) => void;
  selectAggregationFunction: (items: IJSXOption[]) => void;
  selectAggregationColor: (value: unknown) => void;
}

const useFunctionOptions = (layer: Layer): { value: AggregationFunction; text: string }[] => {
  const { t } = useTranslation("dialogs");

  const selectedAggregatedAttribute =
    layer.aggregationAttributes && layer.aggregationAttributes.find(({ name }) => name === layer.aggregateAttr);

  if (!layer.aggregateAttr || !selectedAggregatedAttribute) {
    return [];
  }

  if (
    selectedAggregatedAttribute.type === "Int64" ||
    selectedAggregatedAttribute.type === "Int32" ||
    selectedAggregatedAttribute.type === "Double"
  ) {
    return [
      { value: AggregationFunction.Count, text: t("aggregation.count") },
      { value: AggregationFunction.Min, text: t("aggregation.min") },
      { value: AggregationFunction.Max, text: t("aggregation.max") },
      { value: AggregationFunction.Avg, text: t("aggregation.avg") },
      { value: AggregationFunction.Sum, text: t("aggregation.sum") },
      { value: AggregationFunction.Mod, text: t("aggregation.mod") },
      { value: AggregationFunction.Median, text: t("aggregation.median") },
    ];
  }

  return [{ value: AggregationFunction.Count, text: t("aggregation.count") }];
};

const SecondLine = ({
  layer,
  dialogContainer,
  selectAggregationAttribute,
  selectAggregationFunction,
  selectAggregationColor,
}: SedondLine) => {
  const { t } = useTranslation("dateFilter");

  return (
    <SecondLineLayout>
      <SecondSelectLayerContainer>
        <Select
          value={layer.aggregateAttr}
          label={t("aggregationAttribute")}
          placeholder={t("selectAttribute")}
          options={extractLayerAggrationAttribtesToSelectOptions(layer)}
          onChange={selectAggregationAttribute}
          root={dialogContainer}
          zIndex={100}
        />
        <Select
          value={layer.aggregateFunc}
          label={t("aggregationFunction")}
          placeholder={t("select")}
          options={useFunctionOptions(layer)}
          onChange={selectAggregationFunction}
          root={dialogContainer}
          zIndex={100}
        />
      </SecondSelectLayerContainer>
      <ColorPickerContainer>
        <ColorPicker
          zIndex={100}
          value={layer.aggregationColor}
          onChange={value => {
            selectAggregationColor(value);
          }}
          root={dialogContainer}
        />
      </ColorPickerContainer>
    </SecondLineLayout>
  );
};

type LayerOptions = Partial<Layer> & { text: React.ReactNode }[];

export default ({
  layer,
  options,
  dialogContainer,
  loadingOptions,
}: {
  layer: Layer;
  options: LayerOptions;
  dialogContainer: HTMLElement;
  loadingOptions: boolean;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("dateFilter");
  const { formLayerDispatcher: layerDispatcher, setFormRangeDate } = useContext(DateFilterContext);
  const { minMax, loading } = useGetMaxDateMinDate(layer);
  const [tableFilters, { setTableFilters }] = useTableFilters();

  useSetFirstExistDateRange(minMax);
  const {
    selectLayer,
    selectAttribute,
    toogleAggregation,
    selectAggregationAttribute,
    selectAggregationFunction,
    selectAggregationColor,
  } = useCallbackLayerDispatchers(layer);

  return (
    <Card>
      <Line>
        <Checkbox
          checked={layer.selected}
          onChange={() => {
            layerDispatcher({
              type: ActionLayerKind.ToggleLayer,
              payload: layer.uniqueId,
            });
          }}
        />
        <LayerSelection
          layer={layer}
          selectLayer={selectLayer}
          options={options}
          dialogContainer={dialogContainer}
          loadingOptions={loadingOptions}
          t={t}
        />
        <AttributeSelection
          layer={layer}
          selectAttribute={selectAttribute}
          setFormRangeDate={setFormRangeDate}
          minMax={minMax}
          dialogContainer={dialogContainer}
          loading={loading}
          t={t}
        />
        <IconButton
          kind="date_aggregation"
          onClick={() => {
            toogleAggregation();
          }}
        />
        <IconButton
          kind="delete"
          onClick={() => {
            setTableFilters({
              ...tableFilters,
              [layer.id]: undefined,
            });

            layerDispatcher({ type: ActionLayerKind.DeleteLayer, payload: layer.uniqueId });
            dispatch(clearDateFilterConditions());
          }}
        />
      </Line>
      {layer.isAggregated && (
        <SecondLine
          layer={layer}
          dialogContainer={dialogContainer}
          selectAggregationAttribute={selectAggregationAttribute}
          selectAggregationFunction={selectAggregationFunction}
          selectAggregationColor={selectAggregationColor}
        />
      )}
    </Card>
  );
};
